// /how-we-work: the method as showroom. The Lean Pyramid 6.0 is the spine,
// Maestro is the proof, the Benjamin loop is the close. Screenshots come from
// the demo workspace; captions describe what each screen shows and make
// ships with them wherever the full board is visible.

const howWeWorkContent = {
  'en-US': {
    seo: {
      title: 'How We Work | Lean it 101',
      description:
        'The Lean Pyramid 6.0 operating model: humans set direction, agents do the heavy lifting, and every engagement runs visible in Maestro from day one.',
    },
    hero: {
      kicker: 'How we work',
      headline: 'You will never wonder where the work stands.',
      body: 'Every engagement runs on the Lean Pyramid 6.0, our operating model for the agent era, and stays visible in Maestro, the operating system we built to run it. This page shows you both, exactly as our clients see them.',
    },
    pyramid: {
      kicker: 'The Lean Pyramid 6.0 · Jidoka edition',
      title: 'One model, three layers, humans in charge',
      body: 'Twenty years of Lean and software practice condensed into the question this edition answers: who does the work, and how do humans govern it?',
      apex: 'TRUST',
      base: 'VALUE',
      layers: [
        {
          id: 'strategy',
          name: 'Strategy',
          claim: 'Human intent',
          detail: 'Humans decide why the company exists and where it goes. No agent sets direction.',
          practices: ['Start with purpose', 'Share principles and values', 'Develop people', 'Establish organizational vision', 'Develop breakthrough objectives', 'Deploy strategy'],
          servicesLabel: 'Services on this layer',
          services: [
            { label: 'Strategic planning', href: '/services#strategy' },
            { label: 'Executive advisory', href: '/services#recurring' },
          ],
        },
        {
          id: 'management',
          name: 'Management',
          claim: 'Human policy, executed by the system',
          detail: 'Managers set the policies; the system runs them. Flow is measured only on work items.',
          practices: ['Break down business goals', 'Map the value stream', 'Optimize the product development flow', 'Manage the flow through economics', 'Understand your capacity', 'Make it visual', 'Review and improve relentlessly', 'Keep living standards: the vault'],
          servicesLabel: 'Services on this layer',
          services: [
            { label: 'Workshops and facilitation', href: '/services#workshops' },
            { label: 'Discovery and assessment', href: '/services#discovery' },
          ],
        },
        {
          id: 'engineering',
          name: 'Engineering',
          claim: 'Human craft, agent labor',
          detail: 'Humans own the craft and the judgment. Agents do the heavy lifting under human direction, and their output gets verified.',
          practices: ['Architecture just in time', 'Code to value', 'Keep the code clean', 'Test first, verify continuously', 'Automate the path to production', 'Direct the agents', 'Monitor continuously'],
          servicesLabel: 'Services on this layer',
          services: [
            { label: 'Software delivery and AI transformation', href: '/services#sdlc' },
            { label: 'Forward Deployed Engineering', href: '/forward-deployed-engineering' },
          ],
        },
      ],
    },
    maestro: {
      kicker: 'Maestro · the delivery operating system',
      title: 'The whole engagement on one screen',
      body: 'Our pods and our agents work inside Maestro, and your team reads the same board we do: deadlines with forecasts drawn from measured throughput, decisions waiting on named people, impediments in the open, and a burn-up that admits when a release is late. There is no status to report, because everything already shows. And it is the same operating system you can run without us.',
      link: { label: 'Why delivery needs a new operating system', href: '/maestro' },
      vocabulary: {
        intro: 'The board speaks a small vocabulary:',
        deadlineDone: 'Pilot warehouse live · met',
        deadlineNext: 'Ops team demo · in 4 days',
        wip: 'WIP 3/3',
        late: 'LATE · likely Sep 14',
        outro: 'Deadlines, work in progress, and a release that admits it is running late.',
      },
      shots: [
        {
          src: '/images/maestro/cockpit.jpg',
          alt: 'Maestro cockpit showing a late release with recovery forecast, impediments and decisions',
          caption: 'A release running late says so, with best and worst dates. Decisions wait on named people, and the work log records humans and agents alike.',
          rule: 'Agents get verified. People get trusted.',
        },
        {
          src: '/images/maestro/metrics-flow.jpg',
          alt: 'Maestro metrics tab: cycle time percentiles, weekly throughput, cumulative flow and the value-ordered queue',
          caption: 'Cycle time by size class, throughput with velocity bands, cumulative flow, and the queue ordered by value over measured duration. Where history is short, the charts say so.',
          rule: 'The record measures work items and agent actions. It is never used to measure a person.',
        },
      ],
    },
    loop: {
      kicker: 'The loop',
      title: 'Software that documents itself into your support agent',
      body: 'Teams that build with Maestro and support with Benjamin get a loop nobody else offers: when a feature ships, Maestro turns what changed into documentation and feeds it to Benjamin, so your support agent answers questions about the new feature the week it goes live. Both ends run in production today; the bridge between them is what we are building now, on our own engagements first.',
      steps: [
        { name: 'Problem in', detail: 'The business names the pain; the pod shapes it into work on the board.' },
        { name: 'Governed delivery', detail: 'Pods and agents build under the Pyramid delivery model, every step visible in Maestro.' },
        { name: 'Release', detail: 'The change ships to production through the automated path.' },
        { name: 'Docs become knowledge', detail: 'Maestro writes what shipped into documentation and feeds Benjamin.' },
        { name: 'Customers answered', detail: 'Benjamin resolves questions about the feature, grounded in the fresh manual.' },
      ],
      close: 'That card on the demo board reading "Release notes become Benjamin knowledge automatically" is this loop, exactly as it will run.',
    },
    cta: { title: 'See it on your own problem', body: 'Bring one workflow and we will show you the board it would run on.', label: 'Talk to us' },
  },

  'pt-BR': {
    seo: {
      title: 'Como Trabalhamos | Lean it 101',
      description:
        'O modelo operacional da Pirâmide Lean 6.0: humanos definem a direção, agentes fazem o trabalho pesado, e cada engajamento roda visível no Maestro desde o primeiro dia.',
    },
    hero: {
      kicker: 'Como trabalhamos',
      headline: 'Você nunca vai se perguntar onde o trabalho está.',
      body: 'Cada engajamento roda sobre a Pirâmide Lean 6.0, nosso modelo operacional para a era dos agentes, e permanece visível no Maestro, o sistema operacional que construímos para operá-la. Esta página mostra os dois, exatamente como nossos clientes veem.',
    },
    pyramid: {
      kicker: 'A Pirâmide Lean 6.0 · edição Jidoka',
      title: 'Um modelo, três camadas, humanos no comando',
      body: 'Vinte anos de prática Lean e software condensados na pergunta que esta edição responde: quem faz o trabalho, e como humanos governam?',
      apex: 'CONFIANÇA',
      base: 'VALOR',
      layers: [
        {
          id: 'strategy',
          name: 'Estratégia',
          claim: 'Intenção humana',
          detail: 'Humanos decidem por que a empresa existe e para onde ela vai. Nenhum agente define direção.',
          practices: ['Comece pelo propósito', 'Compartilhe princípios e valores', 'Desenvolva pessoas', 'Estabeleça a visão organizacional', 'Desenvolva objetivos de ruptura', 'Desdobre a estratégia'],
          servicesLabel: 'Serviços nesta camada',
          services: [
            { label: 'Planejamento estratégico', href: '/services#strategy' },
            { label: 'Advisory executivo', href: '/services#recurring' },
          ],
        },
        {
          id: 'management',
          name: 'Gestão',
          claim: 'Política humana, executada pelo sistema',
          detail: 'Gestores definem as políticas; o sistema as executa. O fluxo é medido apenas em itens de trabalho.',
          practices: ['Desdobre metas de negócio', 'Mapeie o fluxo de valor', 'Otimize o fluxo de desenvolvimento', 'Gerencie o fluxo pela economia', 'Conheça sua capacidade', 'Torne visual', 'Revise e melhore sem trégua', 'Mantenha padrões vivos: o vault'],
          servicesLabel: 'Serviços nesta camada',
          services: [
            { label: 'Workshops e facilitação', href: '/services#workshops' },
            { label: 'Discovery e assessment', href: '/services#discovery' },
          ],
        },
        {
          id: 'engineering',
          name: 'Engenharia',
          claim: 'Ofício humano, trabalho de agente',
          detail: 'Humanos são donos do ofício e do julgamento. Agentes fazem o trabalho pesado sob direção humana, e a saída deles é verificada.',
          practices: ['Arquitetura just in time', 'Código a serviço do valor', 'Mantenha o código limpo', 'Teste primeiro, verifique sempre', 'Automatize o caminho até produção', 'Dirija os agentes', 'Monitore continuamente'],
          servicesLabel: 'Serviços nesta camada',
          services: [
            { label: 'Transformação da entrega de software com IA', href: '/services#sdlc' },
            { label: 'Forward Deployed Engineering', href: '/forward-deployed-engineering' },
          ],
        },
      ],
    },
    maestro: {
      kicker: 'Maestro · o sistema operacional da entrega',
      title: 'O engajamento inteiro em uma tela',
      body: 'Nossos pods e nossos agentes trabalham dentro do Maestro, e seu time lê o mesmo board que a gente: deadlines com previsões tiradas do throughput medido, decisões esperando por pessoas nomeadas, impedimentos às claras, e um burn-up que admite quando uma release está atrasada. Não existe status para reportar, porque tudo já aparece. E é o mesmo sistema operacional que você pode rodar sem a gente.',
      link: { label: 'Por que a entrega precisa de um novo sistema operacional', href: '/maestro' },
      vocabulary: {
        intro: 'O board fala um vocabulário pequeno:',
        deadlineDone: 'Pilot warehouse live · cumprido',
        deadlineNext: 'Ops team demo · em 4 dias',
        wip: 'WIP 3/3',
        late: 'LATE · provável 14 set',
        outro: 'Deadlines, trabalho em andamento, e uma release que admite que está atrasada.',
      },
      shots: [
        {
          src: '/images/maestro/cockpit.jpg',
          alt: 'Cockpit do Maestro mostrando uma release atrasada com previsão de recuperação, impedimentos e decisões',
          caption: 'Uma release atrasada diz que está, com datas de melhor e pior caso. Decisões esperam por pessoas nomeadas, e o worklog registra humanos e agentes.',
          rule: 'Agentes são verificados. Pessoas recebem confiança.',
        },
        {
          src: '/images/maestro/metrics-flow.jpg',
          alt: 'Aba de métricas do Maestro: percentis de cycle time, throughput semanal, fluxo cumulativo e a fila ordenada por valor',
          caption: 'Cycle time por classe de tamanho, throughput com faixas de velocidade, fluxo cumulativo, e a fila ordenada por valor sobre duração medida. Onde o histórico é curto, os gráficos dizem isso.',
          rule: 'O registro mede itens de trabalho e ações de agentes. Nunca é usado para medir uma pessoa.',
        },
      ],
    },
    loop: {
      kicker: 'O loop',
      title: 'Software que se documenta dentro do seu agente de suporte',
      body: 'Times que constroem com Maestro e atendem com Benjamin ganham um loop que ninguém mais oferece: quando uma feature entra em produção, o Maestro transforma o que mudou em documentação e alimenta o Benjamin, e seu agente de suporte responde sobre a feature nova na mesma semana do go-live. As duas pontas já rodam em produção; a ponte entre elas é o que estamos construindo agora, primeiro nos nossos próprios engajamentos.',
      steps: [
        { name: 'Problema entra', detail: 'O negócio nomeia a dor; o pod a transforma em trabalho no board.' },
        { name: 'Entrega governada', detail: 'Pods e agentes constroem sob o modelo de entrega da Pirâmide, cada passo visível no Maestro.' },
        { name: 'Release', detail: 'A mudança vai a produção pelo caminho automatizado.' },
        { name: 'Docs viram conhecimento', detail: 'O Maestro escreve o que mudou em documentação e alimenta o Benjamin.' },
        { name: 'Clientes atendidos', detail: 'O Benjamin resolve dúvidas sobre a feature, ancorado no manual recém-criado.' },
      ],
      close: 'Aquele cartão no board de demonstração dizendo "Release notes become Benjamin knowledge automatically" é este loop, exatamente como vai rodar.',
    },
    cta: { title: 'Veja com o seu próprio problema', body: 'Traga um fluxo de trabalho e mostramos o board em que ele rodaria.', label: 'Fale com a gente' },
  },

  es: {
    seo: {
      title: 'Cómo Trabajamos | Lean it 101',
      description:
        'El modelo operativo de la Pirámide Lean 6.0: los humanos marcan la dirección, los agentes hacen el trabajo pesado, y cada compromiso corre visible en Maestro desde el primer día.',
    },
    hero: {
      kicker: 'Cómo trabajamos',
      headline: 'Nunca vas a preguntarte dónde está el trabajo.',
      body: 'Cada compromiso corre sobre la Pirámide Lean 6.0, nuestro modelo operativo para la era de los agentes, y permanece visible en Maestro, el sistema operativo que construimos para operarla. Esta página muestra ambos, exactamente como los ven nuestros clientes.',
    },
    pyramid: {
      kicker: 'La Pirámide Lean 6.0 · edición Jidoka',
      title: 'Un modelo, tres capas, humanos al mando',
      body: 'Veinte años de práctica Lean y software condensados en la pregunta que esta edición responde: quién hace el trabajo, y cómo lo gobiernan los humanos.',
      apex: 'CONFIANZA',
      base: 'VALOR',
      layers: [
        {
          id: 'strategy',
          name: 'Estrategia',
          claim: 'Intención humana',
          detail: 'Los humanos deciden por qué existe la empresa y hacia dónde va. Ningún agente marca la dirección.',
          practices: ['Empieza por el propósito', 'Comparte principios y valores', 'Desarrolla personas', 'Establece la visión organizacional', 'Desarrolla objetivos de ruptura', 'Despliega la estrategia'],
          servicesLabel: 'Servicios en esta capa',
          services: [
            { label: 'Planificación estratégica', href: '/services#strategy' },
            { label: 'Advisory ejecutivo', href: '/services#recurring' },
          ],
        },
        {
          id: 'management',
          name: 'Gestión',
          claim: 'Política humana, ejecutada por el sistema',
          detail: 'Los gestores definen las políticas; el sistema las ejecuta. El flujo se mide solo en ítems de trabajo.',
          practices: ['Despliega las metas de negocio', 'Mapea el flujo de valor', 'Optimiza el flujo de desarrollo', 'Gestiona el flujo por la economía', 'Conoce tu capacidad', 'Hazlo visual', 'Revisa y mejora sin tregua', 'Mantén estándares vivos: el vault'],
          servicesLabel: 'Servicios en esta capa',
          services: [
            { label: 'Workshops y facilitación', href: '/services#workshops' },
            { label: 'Discovery y assessment', href: '/services#discovery' },
          ],
        },
        {
          id: 'engineering',
          name: 'Ingeniería',
          claim: 'Oficio humano, trabajo de agente',
          detail: 'Los humanos son dueños del oficio y del juicio. Los agentes hacen el trabajo pesado bajo dirección humana, y su salida se verifica.',
          practices: ['Arquitectura just in time', 'Código al servicio del valor', 'Mantén el código limpio', 'Prueba primero, verifica siempre', 'Automatiza el camino a producción', 'Dirige a los agentes', 'Monitorea continuamente'],
          servicesLabel: 'Servicios en esta capa',
          services: [
            { label: 'Transformación de la entrega de software con IA', href: '/services#sdlc' },
            { label: 'Forward Deployed Engineering', href: '/forward-deployed-engineering' },
          ],
        },
      ],
    },
    maestro: {
      kicker: 'Maestro · el sistema operativo de la entrega',
      title: 'Todo el compromiso en una pantalla',
      body: 'Nuestros pods y nuestros agentes trabajan dentro de Maestro, y tu equipo lee el mismo tablero que nosotros: deadlines con pronósticos sacados del throughput medido, decisiones esperando a personas con nombre, impedimentos a la vista, y un burn-up que admite cuando una release va tarde. No hay estado que reportar, porque todo ya se ve. Y es el mismo sistema operativo que puedes correr sin nosotros.',
      link: { label: 'Por qué la entrega necesita un nuevo sistema operativo', href: '/maestro' },
      vocabulary: {
        intro: 'El tablero habla un vocabulario pequeño:',
        deadlineDone: 'Pilot warehouse live · cumplido',
        deadlineNext: 'Ops team demo · en 4 días',
        wip: 'WIP 3/3',
        late: 'LATE · probable 14 sep',
        outro: 'Deadlines, trabajo en curso, y una release que admite que va tarde.',
      },
      shots: [
        {
          src: '/images/maestro/cockpit.jpg',
          alt: 'Cockpit de Maestro mostrando una release tarde con pronóstico de recuperación, impedimentos y decisiones',
          caption: 'Una release tarde lo dice, con fechas de mejor y peor caso. Las decisiones esperan a personas con nombre, y el worklog registra humanos y agentes.',
          rule: 'Los agentes se verifican. Las personas reciben confianza.',
        },
        {
          src: '/images/maestro/metrics-flow.jpg',
          alt: 'Pestaña de métricas de Maestro: percentiles de cycle time, throughput semanal, flujo acumulado y la cola ordenada por valor',
          caption: 'Cycle time por clase de tamaño, throughput con bandas de velocidad, flujo acumulado, y la cola ordenada por valor sobre duración medida. Donde la historia es corta, los gráficos lo dicen.',
          rule: 'El registro mide ítems de trabajo y acciones de agentes. Nunca se usa para medir a una persona.',
        },
      ],
    },
    loop: {
      kicker: 'El loop',
      title: 'Software que se documenta dentro de tu agente de soporte',
      body: 'Los equipos que construyen con Maestro y atienden con Benjamin obtienen un loop que nadie más ofrece: cuando una feature llega a producción, Maestro convierte lo que cambió en documentación y alimenta a Benjamin, y tu agente de soporte responde sobre la feature nueva la misma semana del go-live. Ambos extremos ya corren en producción; el puente entre ellos es lo que estamos construyendo ahora, primero en nuestros propios compromisos.',
      steps: [
        { name: 'Entra el problema', detail: 'El negocio nombra el dolor; el pod lo convierte en trabajo en el tablero.' },
        { name: 'Entrega gobernada', detail: 'Pods y agentes construyen bajo el modelo de entrega de la Pirámide, cada paso visible en Maestro.' },
        { name: 'Release', detail: 'El cambio llega a producción por el camino automatizado.' },
        { name: 'Docs se vuelven conocimiento', detail: 'Maestro escribe lo que cambió en documentación y alimenta a Benjamin.' },
        { name: 'Clientes atendidos', detail: 'Benjamin resuelve dudas sobre la feature, anclado en el manual recién creado.' },
      ],
      close: 'Esa tarjeta del tablero de demostración que dice "Release notes become Benjamin knowledge automatically" es este loop, exactamente como va a funcionar.',
    },
    cta: { title: 'Míralo con tu propio problema', body: 'Trae un flujo de trabajo y te mostramos el tablero en el que correría.', label: 'Habla con nosotros' },
  },
}

const normalize = (language) => {
  if (!language) return 'en-US'
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('es')) return 'es'
  return 'en-US'
}

export const getHowWeWorkContent = (language) => howWeWorkContent[normalize(language)]
export default howWeWorkContent
